import { RiSearchLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Card } from "@workspace/ui/components/card";

interface SearchEmptyStateProps {
	searchTerm?: string;
	selectedCity?: string;
	selectedMonth?: string;
	selectedVenue?: string;
	selectedGenres?: string[];
	onClearSearch?: () => void;
	onClearCity?: () => void;
	onClearMonth?: () => void;
	onClearVenue?: () => void;
	onClearGenres?: () => void;
}

export function SearchEmptyState({
	searchTerm,
	selectedCity,
	selectedMonth,
	selectedVenue,
	selectedGenres,
	onClearSearch,
	onClearCity,
	onClearMonth,
	onClearVenue,
	onClearGenres,
}: SearchEmptyStateProps) {
	const hasSearchTerm = searchTerm?.trim();
	const hasCityFilter = selectedCity && selectedCity !== "all";
	const hasMonthFilter = selectedMonth && selectedMonth !== "all";
	const hasVenueFilter = selectedVenue && selectedVenue !== "all";
	const hasGenreFilter = selectedGenres && selectedGenres.length > 0;

	const hasAnyFilter =
		hasSearchTerm ||
		hasCityFilter ||
		hasMonthFilter ||
		hasVenueFilter ||
		hasGenreFilter;
	const hasMultipleFilters =
		[
			hasSearchTerm,
			hasCityFilter,
			hasMonthFilter,
			hasVenueFilter,
			hasGenreFilter,
		].filter(Boolean).length > 1;

	let message = "No shows found";

	if (hasMultipleFilters) {
		message = "Try adjusting your filters to see more shows.";
	} else if (hasSearchTerm) {
		message = `No shows match "${searchTerm}".`;
	} else if (hasCityFilter) {
		message = `No shows in ${selectedCity}.`;
	} else if (hasMonthFilter) {
		message = `No shows in ${selectedMonth}.`;
	} else if (hasVenueFilter) {
		message = "No shows at this venue.";
	} else if (hasGenreFilter) {
		message = `No shows matching ${selectedGenres.length === 1 ? "this genre" : "these genres"}.`;
	}

	return (
		<Card className="p-8 text-center">
			<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
				<RiSearchLine className="h-6 w-6 text-muted-foreground" />
			</div>
			<h3 className="mb-1 font-semibold text-xl">No Shows Found</h3>
			<p
				className={`text-muted-foreground text-sm ${hasAnyFilter ? "mb-4" : ""}`}
			>
				{message}
			</p>

			{hasAnyFilter && (
				<div className="flex flex-wrap justify-center gap-2">
					{hasSearchTerm && onClearSearch && (
						<Button
							variant="secondary"
							size="sm"
							onClick={onClearSearch}
						>
							Clear Search
						</Button>
					)}
					{hasCityFilter && onClearCity && (
						<Button
							variant="secondary"
							size="sm"
							onClick={onClearCity}
						>
							All Cities
						</Button>
					)}
					{hasMonthFilter && onClearMonth && (
						<Button
							variant="secondary"
							size="sm"
							onClick={onClearMonth}
						>
							All Months
						</Button>
					)}
					{hasVenueFilter && onClearVenue && (
						<Button
							variant="secondary"
							size="sm"
							onClick={onClearVenue}
						>
							All Venues
						</Button>
					)}
					{hasGenreFilter && onClearGenres && (
						<Button
							variant="secondary"
							size="sm"
							onClick={onClearGenres}
						>
							All Genres
						</Button>
					)}
				</div>
			)}
		</Card>
	);
}
